import React, { useContext } from 'react';
import url from 'url';
import { graphql, useStaticQuery } from 'gatsby';
import { Container, Grid } from 'semantic-ui-react';
import InlineSvg from '../InlineSvg';
import dayjs from 'dayjs';
import Link from '../Link';
import logo from '../../assets/images/logo-white.svg';

import { LargeMonitorContext } from '../../context';

const Footer = () => {
  const data = useStaticQuery(graphql`
    query footerMenu {
      wpMenu(slug: { eq: "footer" }) {
        name
        count
        menuItems {
          nodes {
            databaseId
            order
            label
            url
            target
            cssClasses
          }
        }
      }
      wp {
        acfOptionsSiteSettings {
          footerOptions {
            acknowledgement {
              text
              images {
                image {
                  sourceUrl
                  altText
                }
              }
              textBelowImages
              bottomImages {
                image {
                  sourceUrl
                  altText
                }
              }
            }
            contact {
              text
              heading
              link {
                target
                title
                url
              }
            }
            newsletter {
              buttonText
              heading
              text
            }
          }
        }
      }
    }
  `);
  const {
    wp: {
      acfOptionsSiteSettings: { footerOptions },
    },
  } = data;
  const { contact } = footerOptions;
  const { acknowledgement } = footerOptions;
  const { link: contactLink } = contact;

  const largeMonitorContext = useContext(LargeMonitorContext);

  return (
    <section className="footer__wrapper">
      <Container>
        <Grid stackable className="footer">
          <div className="footer__acknowledgement--wrapper">
            <div className="footer__acknowledgement--content">
              <div className="footer__acknowledgement--top-text">
                <p>{acknowledgement?.text}</p>
              </div>

              {acknowledgement?.images ? (
                <div className="footer__acknowledgement--image">
                  {acknowledgement.images.map((image, i) => {
                    return (
                      <div key={i}>
                        <img
                          src={`${image.image.sourceUrl}`}
                          alt={
                            image.image.altText
                              ? image.image.altText
                              : 'inclusivity flag'
                          }
                        ></img>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              <div className="footer__acknowledgement--bottom-text">
                <p>{acknowledgement?.textBelowImages}</p>
              </div>

              {acknowledgement?.bottomImages ? (
                <div className="footer__acknowledgement--image">
                  {acknowledgement.bottomImages.map((image, i) => {
                    return (
                      <div key={i}>
                        <img
                          src={`${image.image.sourceUrl}`}
                          alt={
                            image.image.altText
                              ? image.image.altText
                              : 'inclusivity flag'
                          }
                        ></img>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <div className="footer__divider"></div>
          </div>

          <Grid.Row className="footer__first-row">
            {largeMonitorContext && <Grid.Column width={1}></Grid.Column>}
            {/* menu */}
            <Grid.Column
              className="footer__menu-column"
              mobile={12}
              tablet={largeMonitorContext ? 3 : 6}
              computer={largeMonitorContext ? 3 : 6}
              largeScreen={largeMonitorContext ? 3 : 6}
            >
              <ul className="footer__menu">
                {data.wpMenu.menuItems.nodes.map((item) => (
                  <li key={item.databaseId} className="footer__menu-item">
                    <Link to={url.parse(item.url).pathname}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </Grid.Column>
            <Grid.Column
              className="footer__contact-column"
              mobile={12}
              tablet={largeMonitorContext ? 5 : 6}
              computer={largeMonitorContext ? 5 : 6}
              largeScreen={largeMonitorContext ? 5 : 6}
            >
              {/* contact us */}
              <h4>{contact.heading}</h4>
              <p>{contact.text}</p>
              <Link
                className="footer__contact-link no-decoration"
                to={contactLink.url}
              >
                {contactLink.title}
              </Link>
            </Grid.Column>
            {largeMonitorContext && <Grid.Column width={1}></Grid.Column>}
          </Grid.Row>
          <Grid.Row>
            {largeMonitorContext && <Grid.Column width={1}></Grid.Column>}
            <Grid.Column width={largeMonitorContext ? 10 : 12}>
              <div className="footer__divider"></div>
            </Grid.Column>
            {largeMonitorContext && <Grid.Column width={1}></Grid.Column>}
          </Grid.Row>
          <div className="footer__second-row">
            {/* logo */}

            <div className="footer__logo-column">
              <img src={logo} className="footer__logo" alt="logo" />
            </div>

            <div className="footer__copyright-social-group">
              {/* copyright */}
              <div className="footer__copyright-column">
                © {dayjs().year()} CitiPower and Powercor Australia
              </div>
              {/* socials */}
              <div className="footer__social-column">
                <ul className="footer__socials-list">
                  <li>
                    <Link
                      to="https://www.facebook.com/citipower.powercor"
                      target="_blank"
                      title="Find us on Facebook"
                    >
                      <InlineSvg name="facebook" color="white" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="https://x.com/PowercorAust"
                      target="_blank"
                      title="Find us on X"
                    >
                      <InlineSvg name="twitter" color="white" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="https://www.youtube.com/user/CitiPowerPowercor"
                      target="_blank"
                      title="Find us Youtube"
                    >
                      <InlineSvg name="youtube" color="white" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="https://www.linkedin.com/company/citipower-and-powercor/?originalSubdomain=au"
                      target="_blank"
                      title="Find us on LinkedIn"
                    >
                      <InlineSvg name="linkedin" color="white" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="https://www.instagram.com/citipowerpowercor" 
                      target="_blank"
                      title="Find us on Instagram"
                    >
                      <InlineSvg name="instagram" color="white" />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Grid>
      </Container>
    </section>
  );
};

export default Footer;
